import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, DoorOpen, Loader2, ArrowRight, Check, Send, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SEO from '@/components/SEO';
import { track } from '@/lib/track';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badgeStyles = {
  'Studios Available': { bg: 'bg-[#E8EDE5] text-[#3A4035]', dot: 'bg-[#3A4035]' },
  'Limited Availability': { bg: 'bg-[#F4EFE6] text-[#7A6849]', dot: 'bg-[#C4A44E]' },
  'Fully Occupied': { bg: 'bg-[#F2F0EB] text-[#8A8B85]', dot: 'bg-[#8A8B85]' },
  'Opening Soon': { bg: 'bg-[#E8EDF2] text-[#4A6080]', dot: 'bg-[#4A6080]' },
  'Coming Soon': { bg: 'bg-[#F4EFE6] text-[#7A6849]', dot: 'bg-[#C4A44E]' },
};

export default function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/locations`).then(r => setLocations(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="locations-page">
      <SEO title="Locations" description="Find a private studio near you. Explore available studios across London for health, wellbeing and beauty practitioners." path="/locations" />
      {/* Header */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A1A1A] tracking-tight mb-4">
            Find a studio near you
          </h1>
          <p className="text-base text-[#5C5C55] max-w-xl mx-auto leading-relaxed">
            Explore available studios across our locations. Each space is designed for independent practitioners who want a professional, private environment to run their business.
          </p>
        </div>
      </section>

      {/* Locations grid */}
      <section className="pb-16 sm:pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          {loading && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {locations.map((loc, i) => {
              const style = badgeStyles[loc.badge] || badgeStyles['Studios Available'];
              const isLive = loc.lifecycle === 'live';
              const isLaunching = loc.lifecycle === 'launching_soon';
              const isComing = loc.lifecycle === 'coming_soon';
              const isOccupied = loc.badge === 'Fully Occupied';
              return (
                <div key={loc.site_id}
                  className={`bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 animate-fade-in stagger-${i + 1}`}
                  data-testid={`location-card-${loc.site_id}`}>
                  <div className="p-6 sm:p-8 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-4">
                      <h2 className="font-heading text-xl font-medium text-[#1A1A1A]">{loc.name}</h2>
                      <span className={`text-[10px] px-3 py-1.5 rounded-full font-semibold shrink-0 flex items-center gap-1.5 ${style.bg}`} data-testid={`badge-${loc.site_id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        {loc.badge}
                      </span>
                    </div>

                    {loc.opening_date && (
                      <p className="text-sm font-medium text-[#3A4035] mb-3">
                        {loc.lifecycle === 'coming_soon' ? `Planned for ${loc.opening_date}` : `Opening ${loc.opening_date}`}
                      </p>
                    )}

                    <div className="flex items-center gap-1.5 text-sm text-[#5C5C55] mb-4">
                      <MapPin className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                      {loc.address}{loc.city ? `, ${loc.city}` : ''} {loc.postcode}
                    </div>

                    {loc.description && <p className="text-sm text-[#8A8B85] mb-4 leading-relaxed">{loc.description}</p>}

                    <p className="text-sm text-[#5C5C55]/60 mb-3">
                      {isOccupied ? 'Join the waiting list — spaces are released as they become available.'
                        : isComing ? 'Register your interest and we\'ll let you know when we launch.'
                        : 'Each studio is designed for one independent business.'}
                    </p>

                    {(isLive || isLaunching) && <p className="text-xs text-[#8A8B85] mb-5">Clear pricing, with inclusions and any additional charges explained before you commit.</p>}
                    {isComing && <p className="text-xs text-[#8A8B85] mb-5">&nbsp;</p>}

                    {/* CTA button — pinned to bottom */}
                    <div className="mt-auto pt-2">
                    {loc.cta === 'book_viewing' && (
                      <Link to={`/book-a-viewing?location=${encodeURIComponent(loc.name)}`}
                        className="w-full flex items-center justify-center gap-2 bg-[#3A4035] text-white rounded-full py-3 text-sm font-medium hover:bg-[#2B3027] transition-colors"
                        data-testid={`location-cta-${loc.site_id}`}>
                        {loc.cta_label} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                      </Link>
                    )}
                    {loc.cta === 'join_waitlist' && (
                      <Link to={`/book-a-viewing?location=${encodeURIComponent(loc.name)}`}
                        className="w-full flex items-center justify-center gap-2 bg-[#3A4035] text-white rounded-full py-3 text-sm font-medium hover:bg-[#2B3027] transition-colors"
                        data-testid={`location-cta-${loc.site_id}`}>
                        {loc.cta_label} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                      </Link>
                    )}
                    {loc.cta === 'register_interest' && (
                      <button onClick={() => {
                        const el = document.querySelector('[data-testid="register-interest-section"]');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                        className="w-full flex items-center justify-center gap-2 border border-[#E5E2DC] text-[#3A4035] rounded-full py-3 text-sm font-medium hover:bg-[#F2F0EB] transition-colors"
                        data-testid={`location-cta-${loc.site_id}`}>
                        {loc.cta_label} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!loading && locations.length === 0 && (
            <div className="text-center py-16">
              <DoorOpen className="w-10 h-10 text-[#D8DBD3] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-[#8A8B85]">Locations coming soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Register Interest Section */}
      <RegisterInterestSection />
    </div>
  );
}

function RegisterInterestSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', preferred_location: '', business_type: '' });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setError('Name and email are required'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/public/location-interest`, form);
      track('location_interest_submitted', { location: form.preferred_location });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <section className="pb-16 sm:pb-24" data-testid="register-interest-section">
      <div className="max-w-lg mx-auto px-4 sm:px-8">
        <div className="bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6 sm:p-8">
          {done ? (
            <div className="text-center py-4 animate-fade-in" data-testid="interest-success">
              <div className="w-12 h-12 rounded-2xl bg-[#E8EDE5] flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-[#3A4035]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">You're on the list</h3>
              <p className="text-sm text-[#5C5C55]">We'll be in touch as soon as we have something nearby.</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="font-heading text-xl sm:text-2xl font-medium text-[#1A1A1A] tracking-tight mb-2">
                  Don't see your location?
                </h2>
                <p className="text-sm text-[#5C5C55] leading-relaxed">
                  Register your interest and we'll let you know when we launch nearby.
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4] mb-5" data-testid="interest-error">
                  <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} />
                  <p className="text-xs text-[#B33A3A]">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Full Name <span className="text-[#B33A3A]">*</span></Label>
                  <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your full name"
                    className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="interest-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Email <span className="text-[#B33A3A]">*</span></Label>
                    <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@example.com"
                      className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="interest-email" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Phone</Label>
                    <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="07700 000000"
                      className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="interest-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Preferred Location</Label>
                    <Input value={form.preferred_location} onChange={e => set('preferred_location', e.target.value)} placeholder="e.g. Clapham, Brixton, Putney"
                      className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="interest-location" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Service Type</Label>
                    <Input value={form.business_type} onChange={e => set('business_type', e.target.value)} placeholder="e.g. Massage, Beauty"
                      className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="interest-service" />
                  </div>
                </div>
                <Button type="submit" disabled={saving || !form.name.trim() || !form.email.trim()}
                  className="w-full bg-[#3A4035] text-white rounded-full h-11 text-sm font-medium hover:bg-[#2B3027]"
                  data-testid="interest-submit">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending...</> : <>Register Interest</>}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
